import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useCountdown from '../hooks/useCountdown';

const Hero = () => {
  const navigate = useNavigate();
  
  // Voting period: Sunday Nov 23, 2025 9:00 AM to Thursday Nov 25, 2025 6:00 PM
  const votingStartTime = useMemo(() => new Date('2025-11-23T09:00:00'), []);
  const votingEndTime = useMemo(() => new Date('2025-11-25T18:00:00'), []);
  
  // Array of all hero images
  const slides = [
    'https://customer-assets.emergentagent.com/job_bf75f1a2-78b0-4c68-8a8d-f5f1b6035437/artifacts/0e3kh7m5_IMG-20251109-WA0013.jpg',
    'https://customer-assets.emergentagent.com/job_bf75f1a2-78b0-4c68-8a8d-f5f1b6035437/artifacts/oq3vs8km_IMG-20251109-WA0017.jpg',
    'https://customer-assets.emergentagent.com/job_bf75f1a2-78b0-4c68-8a8d-f5f1b6035437/artifacts/nmw88rf3_IMG-20251109-WA0016.jpg',
    'https://customer-assets.emergentagent.com/job_bf75f1a2-78b0-4c68-8a8d-f5f1b6035437/artifacts/80nw4dza_IMG-20251109-WA0015.jpg',
    'https://customer-assets.emergentagent.com/job_bf75f1a2-78b0-4c68-8a8d-f5f1b6035437/artifacts/ycclgtc4_IMG-20251109-WA0019.jpg'
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [votingStatus, setVotingStatus] = useState('before'); // 'before', 'active', 'ended'
  const countdown = useCountdown(votingEndTime);

  // Check voting status
  useEffect(() => {
    const checkVotingStatus = () => {
      const now = new Date();
      if (now < votingStartTime) {
        setVotingStatus('before');
      } else if (now >= votingStartTime && now <= votingEndTime) {
        setVotingStatus('active');
      } else {
        setVotingStatus('ended');
      }
    };

    checkVotingStatus();
    const interval = setInterval(checkVotingStatus, 1000);
    return () => clearInterval(interval);
  }, [votingStartTime, votingEndTime]);

  // Redirect to results if voting ended
  useEffect(() => {
    if (votingStatus === 'ended') {
      navigate('/results');
    }
  }, [votingStatus, navigate]);

  // Auto-rotate slides every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length]);

  const handleVotingClick = (e) => {
    e.preventDefault();
    if (votingStatus === 'active') {
      navigate('/voting');
    }
  };

  const handleCandidatesClick = (e) => {
    e.preventDefault();
    const targetElement = document.querySelector('#about');
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetTop - 80,
        behavior: 'smooth'
      });
    }
  };

  // Calculate time until voting starts (for display)
  const getTimeUntilStart = useCallback(() => {
    const now = new Date();
    const diff = votingStartTime - now;
    if (diff <= 0) return null;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  }, [votingStartTime]);

  const [timeUntilStart, setTimeUntilStart] = useState(getTimeUntilStart());

  useEffect(() => {
    if (votingStatus === 'before') {
      const interval = setInterval(() => {
        setTimeUntilStart(getTimeUntilStart());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [votingStatus, getTimeUntilStart]);

  return (
    <section className="hero" id="home">
      <div className="hero-background">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${slide})` }}
          ></div>
        ))}
        <div className="hero-overlay"></div>
      </div>
      <div className="hero-content">
        <h1>انتخابات انجمن‌های علمی دانشگاه شمال آمل ۱۴۰۴</h1>
        <p>صدای تو، مسیر پیشرفت علمی!</p>
        <div className="hero-buttons">
          <button
            className={`btn btn-primary ${votingStatus !== 'active' ? 'disabled' : ''}`}
            onClick={handleVotingClick}
            disabled={votingStatus !== 'active'}
            title={votingStatus === 'before' ? 'رای‌گیری هنوز شروع نشده است' : ''}
          >
            {votingStatus === 'before' ? '⏳ رای‌گیری به زودی...' : 'ورود به رأی‌گیری'}
          </button>
          <a href="#about" className="btn btn-secondary" onClick={handleCandidatesClick}>مشاهده نامزدها</a>
        </div>
        
        {/* Countdown Timer */}
        {votingStatus === 'before' && timeUntilStart && (
          <div className="countdown-container before-start">
            <div className="countdown-title">⏰ زمان باقی‌مانده تا شروع رای‌گیری</div>
            <div className="countdown-subtitle">یکشنبه ۰۲ آذر ۱۴۰۴ - ساعت ۹:۰۰ صبح</div>
            <div className="countdown" id="countdown">
              <div className="countdown-item">
                <span className="countdown-value">{timeUntilStart.days}</span>
                <span className="countdown-label">روز</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{timeUntilStart.hours}</span>
                <span className="countdown-label">ساعت</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{timeUntilStart.minutes}</span>
                <span className="countdown-label">دقیقه</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{timeUntilStart.seconds}</span>
                <span className="countdown-label">ثانیه</span>
              </div>
            </div>
          </div>
        )}

        {votingStatus === 'active' && (
          <div className="countdown-container">
            <div className="countdown-title">زمان باقی‌مانده تا پایان انتخابات</div>
            <div className="countdown" id="countdown">
              <div className="countdown-item">
                <span className="countdown-value">{countdown.days}</span>
                <span className="countdown-label">روز</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{countdown.hours}</span>
                <span className="countdown-label">ساعت</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{countdown.minutes}</span>
                <span className="countdown-label">دقیقه</span>
              </div>
              <div className="countdown-item">
                <span className="countdown-value">{countdown.seconds}</span>
                <span className="countdown-label">ثانیه</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;